/**
 * The publish step checklist in the management UI (Registry page, per attempt).
 *
 * internal/publish (src/stepChecklist.ts) sends the run's checklist as `steps`
 * on every pipeline event; the broker stores it as JSON on
 * `publish_versions.steps` (private/auth migration 0083) and in the run's
 * history entry. This file only parses it defensively, decides what is
 * "wrong" (degraded / failed), and shapes rows and the banner text — the
 * words (reasons, build labels) are the publisher's, shown verbatim
 * (plans/2026-10-14-publish-checklist-and-job-journal.md).
 *
 * Backfill-tolerant: a row from before the migration, or from an older
 * publisher, has no steps → parseSteps returns null and the page says
 * "no step detail".
 */
import { fmtDurationMs } from "./buildLabels.ts";
import { darwinRecordFor } from "./nativeTargets.ts";

export type StepState =
  "pending" | "running" | "ok" | "degraded" | "failed" | "skipped";
export type StepGroup = "pipeline" | "optimisation" | "environment";

export interface StepBuild {
  seq?: number;
  kind: string;
  label: string;
  jobId?: string;
  local?: boolean;
  status: string;
  startedAt?: number;
  endedAt?: number;
}

export interface StepRecord {
  id: string;
  group: StepGroup;
  name: string;
  state: StepState;
  startedAt?: number;
  endedAt?: number;
  durationMs?: number;
  builds?: StepBuild[];
  reason?: string;
  /** A running step's progress (the benchmark). */
  progress?: StepProgress;
  /** package-test / full-project: when the run (after the compile) began. */
  runStartedAt?: number;
  /** Facts shown under "details" (publish build name / integrity, text-order
   *  derivation, benchmark schema / headline). */
  facts?: Record<string, string | number | boolean>;
  /** The benchmark's per-view results. */
  views?: StepView[];
}

export interface StepView {
  libc: string;
  runtime: string;
  state: "ok" | "partial" | "unavailable";
  reason?: string;
  headline?: string;
}

function parseFacts(x: unknown): StepRecord["facts"] | undefined {
  if (!x || typeof x !== "object" || Array.isArray(x)) return undefined;
  const out: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(x as Record<string, unknown>))
    if (
      typeof v === "string" ||
      typeof v === "number" ||
      typeof v === "boolean"
    )
      out[k] = v;
  return Object.keys(out).length ? out : undefined;
}

function parseViews(x: unknown): StepView[] | undefined {
  if (!Array.isArray(x)) return undefined;
  const out: StepView[] = [];
  for (const v of x) {
    if (!v || typeof v !== "object") continue;
    const r = v as Record<string, unknown>;
    if (typeof r["libc"] !== "string" || typeof r["runtime"] !== "string")
      continue;
    const state =
      r["state"] === "ok" ||
      r["state"] === "partial" ||
      r["state"] === "unavailable"
        ? r["state"]
        : null;
    if (!state) continue;
    out.push({
      libc: r["libc"],
      runtime: r["runtime"],
      state,
      ...(typeof r["reason"] === "string" && r["reason"]
        ? { reason: r["reason"] }
        : {}),
      ...(typeof r["headline"] === "string" && r["headline"]
        ? { headline: r["headline"] }
        : {}),
    });
  }
  return out.length ? out : undefined;
}

export interface StepProgress {
  view: string;
  viewsDone: number;
  viewsTotal: number;
  workload?: string;
  workloadsDone: number;
  workloadsTotal: number;
}

function parseProgress(x: unknown): StepProgress | undefined {
  if (!x || typeof x !== "object") return undefined;
  const p = x as Record<string, unknown>;
  const n = (v: unknown) =>
    typeof v === "number" && Number.isFinite(v) ? v : null;
  if (typeof p["view"] !== "string") return undefined;
  const viewsDone = n(p["viewsDone"]);
  const viewsTotal = n(p["viewsTotal"]);
  const workloadsDone = n(p["workloadsDone"]);
  const workloadsTotal = n(p["workloadsTotal"]);
  if (
    viewsDone === null ||
    viewsTotal === null ||
    workloadsDone === null ||
    workloadsTotal === null
  )
    return undefined;
  return {
    view: p["view"],
    viewsDone,
    viewsTotal,
    workloadsDone,
    workloadsTotal,
    ...(typeof p["workload"] === "string" ? { workload: p["workload"] } : {}),
  };
}

const STATES: readonly StepState[] = [
  "pending",
  "running",
  "ok",
  "degraded",
  "failed",
  "skipped",
];
const GROUPS: readonly StepGroup[] = [
  "pipeline",
  "optimisation",
  "environment",
];

/** The group headings, in display order. */
export const GROUP_TITLES: Record<StepGroup, string> = {
  pipeline: "Pipeline",
  optimisation: "Optimisation",
  environment: "Benchmark environment",
};

export const STATE_LABEL: Record<StepState, string> = {
  pending: "pending",
  running: "running",
  ok: "ok",
  degraded: "DEGRADED",
  failed: "FAILED",
  skipped: "skipped",
};

/** Badge variant per state (matches ui/badge variants used by the page). */
export const STATE_VARIANT: Record<
  StepState,
  "secondary" | "success" | "warning" | "destructive" | "info" | "outline"
> = {
  pending: "outline",
  running: "info",
  ok: "success",
  degraded: "warning",
  failed: "destructive",
  skipped: "secondary",
};

/** `steps` (JSON text from D1, or the already-parsed array of a history
 *  entry) → records; null when absent or unreadable ("no step detail"). */
export function parseSteps(raw: unknown): StepRecord[] | null {
  let v: unknown = raw;
  if (typeof raw === "string") {
    if (!raw) return null;
    try {
      v = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!Array.isArray(v)) return null;
  const out: StepRecord[] = [];
  for (const x of v) {
    if (!x || typeof x !== "object") continue;
    const r = x as Record<string, unknown>;
    if (typeof r["id"] !== "string" || typeof r["name"] !== "string") continue;
    if (!STATES.includes(r["state"] as StepState)) continue;
    const group = GROUPS.includes(r["group"] as StepGroup)
      ? (r["group"] as StepGroup)
      : "pipeline";
    const builds = Array.isArray(r["builds"])
      ? (r["builds"] as unknown[]).filter(
          (b): b is StepBuild =>
            !!b &&
            typeof b === "object" &&
            typeof (b as StepBuild).kind === "string" &&
            typeof (b as StepBuild).label === "string",
        )
      : undefined;
    out.push({
      id: r["id"],
      group,
      name: r["name"],
      state: r["state"] as StepState,
      ...(typeof r["startedAt"] === "number"
        ? { startedAt: r["startedAt"] }
        : {}),
      ...(typeof r["endedAt"] === "number" ? { endedAt: r["endedAt"] } : {}),
      ...(typeof r["durationMs"] === "number"
        ? { durationMs: r["durationMs"] }
        : {}),
      ...(builds && builds.length ? { builds } : {}),
      ...(typeof r["reason"] === "string" && r["reason"]
        ? { reason: r["reason"] }
        : {}),
      ...(parseProgress(r["progress"])
        ? { progress: parseProgress(r["progress"])! }
        : {}),
      ...(typeof r["runStartedAt"] === "number"
        ? { runStartedAt: r["runStartedAt"] }
        : {}),
      ...(parseFacts(r["facts"]) ? { facts: parseFacts(r["facts"])! } : {}),
      ...(parseViews(r["views"]) ? { views: parseViews(r["views"])! } : {}),
    });
  }
  return out.length ? out : null;
}

/** The decoupled darwin follow-up keeps going after the run ended, so its
 *  row follows the row's native_targets record (which the follow-up updates)
 *  when there is one. */
export function withDarwinFromTargets(
  steps: StepRecord[],
  nativeTargets: string | null | undefined,
  attemptStatus?: string | null,
): StepRecord[] {
  // Before this attempt's publish the record is a previous attempt's: not
  // overlaid (see nativeTargets.ts PRE_PUBLISH_STATUSES).
  const t = darwinRecordFor(nativeTargets, attemptStatus).current;
  if (!t) return steps;
  const state: StepState =
    t.status === "shipped"
      ? "ok"
      : t.status === "pending"
        ? "running"
        : t.status === "failed"
          ? "failed"
          : "skipped";
  return steps.map((s) =>
    s.id === "darwin-followup"
      ? {
          ...s,
          state,
          reason:
            t.reason ??
            (t.status === "shipped" && t.publishedIn
              ? `shipped in ${t.publishedIn}`
              : s.reason),
          ...(t.jobId && !(s.builds ?? []).some((b) => b.jobId === t.jobId)
            ? {
                builds: [
                  ...(s.builds ?? []),
                  {
                    kind: "darwin-followup",
                    label: "darwin arm64 follow-up",
                    jobId: t.jobId,
                    status: t.status === "shipped" ? "succeeded" : t.status,
                  },
                ],
              }
            : {}),
        }
      : s,
  );
}

/** Rows that need attention: degraded or failed. */
export function problemSteps(steps: readonly StepRecord[]): StepRecord[] {
  return steps.filter((s) => s.state === "degraded" || s.state === "failed");
}

/** The banner lines of an attempt: "<name> DEGRADED: <first reason line>".
 *  Empty when nothing is wrong. */
export function bannerLines(steps: readonly StepRecord[]): string[] {
  return problemSteps(steps).map((s) => {
    const first = (s.reason ?? "").split("\n")[0]!.slice(0, 400);
    return `${s.name} ${STATE_LABEL[s.state]}${first ? `: ${first}` : ""}`;
  });
}

export interface BuildLink {
  label: string;
  jobId: string | null;
  seqText: string | null;
  status: string;
  local: boolean;
}

export interface StepRow {
  id: string;
  group: StepGroup;
  name: string;
  state: StepState;
  stateLabel: string;
  /** "2m 03s"; null for a row that never started. A running row shows its
   *  elapsed time so far. */
  duration: string | null;
  builds: BuildLink[];
  reason: string | null;
  /** "Node 26 · Alpine musl · view 5/6 · workload 4/12 (parse)" while running. */
  progressText: string | null;
}

/** "Node 26 · Alpine musl · view 5/6 · workload 4/12 (parse)". The view
 *  number is the one being measured (done + 1). */
export function progressText(p: StepProgress): string {
  const view = Math.min(p.viewsDone + 1, p.viewsTotal);
  const parts = [p.view, `view ${view}/${p.viewsTotal}`];
  if (p.workloadsTotal > 0)
    parts.push(
      `workload ${Math.min(p.workloadsDone + 1, p.workloadsTotal)}/${p.workloadsTotal}${p.workload ? ` (${p.workload})` : ""}`,
    );
  return parts.join(" · ");
}

/** The benchmark as a first-class stage of an attempt. */
export type BenchmarkState =
  "pending" | "running" | "done" | "degraded" | "failed" | "skipped";

export interface BenchmarkStage {
  state: BenchmarkState;
  reason: string | null;
  startedAt: number | null;
  progress: string | null;
}

/** The benchmark row of a checklist; null when the attempt has no step
 *  detail (an older publisher — the strip then shows no benchmark pill). */
export function benchmarkStage(steps: unknown): BenchmarkStage | null {
  const parsed = parseSteps(steps);
  const b = parsed?.find((s) => s.id === "benchmark");
  if (!b) return null;
  return {
    state: b.state === "ok" ? "done" : b.state,
    reason: b.reason ?? null,
    startedAt: b.startedAt ?? null,
    progress:
      b.state === "running" && b.progress ? progressText(b.progress) : null,
  };
}

/** "09:41" in the viewer's local time. */
function hhmm(ms: number): string {
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/** The version status text: "published · benchmarking" while the benchmark
 *  of a published attempt runs, plain "published" otherwise. */
export function publishedLabel(stage: BenchmarkStage | null): string {
  return stage?.state === "running" ? "published · benchmarking" : "published";
}

/** Banner while a published attempt's benchmark is still running; null
 *  otherwise. `updatedAtMs` (the row's last event) flags a stalled one. */
export function benchmarkBanner(
  stage: BenchmarkStage | null,
  nowMs: number,
  updatedAtMs?: number,
): string | null {
  if (stage?.state !== "running") return null;
  const parts = [
    `published — benchmark still running${stage.startedAt ? ` (started ${hhmm(stage.startedAt)})` : ""}`,
  ];
  if (stage.progress) parts.push(stage.progress);
  if (updatedAtMs && nowMs - updatedAtMs > 15 * 60_000)
    parts.push(
      `no progress update for ${Math.round((nowMs - updatedAtMs) / 60_000)} min (the publish service may have restarted)`,
    );
  return parts.join(" · ");
}

/** Wall time of the whole attempt, benchmark included: earliest row start
 *  to the latest row end (or `nowMs` while anything runs); null when no
 *  row has timing. */
export function totalElapsedMs(
  steps: readonly StepRecord[],
  nowMs: number,
): number | null {
  const starts = steps.flatMap((s) => (s.startedAt ? [s.startedAt] : []));
  if (starts.length === 0) return null;
  // The decoupled darwin follow-up outlives the run: it never keeps the
  // attempt's clock running.
  const running = steps.some(
    (s) => s.state === "running" && s.id !== "darwin-followup",
  );
  const ends = steps.flatMap((s) => (s.endedAt ? [s.endedAt] : []));
  const end = running ? nowMs : ends.length ? Math.max(...ends) : nowMs;
  return Math.max(0, end - Math.min(...starts));
}

/** View model: the rows of one group-ordered checklist. */
export function stepRows(
  steps: readonly StepRecord[],
  nowMs: number = Date.now(),
): StepRow[] {
  const order = (g: StepGroup) => GROUPS.indexOf(g);
  return steps
    .map((s, i) => ({ s, i }))
    .sort((a, b) => order(a.s.group) - order(b.s.group) || a.i - b.i)
    .map(({ s }) => ({
      id: s.id,
      group: s.group,
      name: s.name,
      state: s.state,
      stateLabel:
        s.id === "benchmark" && s.state === "ok"
          ? "done"
          : STATE_LABEL[s.state],
      progressText:
        s.state === "running" && s.progress ? progressText(s.progress) : null,
      duration:
        s.durationMs !== undefined
          ? fmtDurationMs(1, 1 + s.durationMs)
          : s.state === "running" && s.startedAt
            ? fmtDurationMs(s.startedAt, undefined, nowMs)
            : null,
      builds: (s.builds ?? []).map((b) => ({
        label: b.label,
        jobId: b.jobId ?? null,
        seqText: b.seq ? `#${b.seq}` : null,
        status: b.status,
        local: !!b.local,
      })),
      reason: s.reason ?? null,
    }));
}

/** Plain-text copy of a checklist (for the "copy" button / bug reports). */
export function checklistText(steps: readonly StepRecord[]): string {
  return stepRows(steps)
    .map((r) => {
      const builds = r.builds
        .map((b) => `${b.label}${b.jobId ? ` job ${b.jobId}` : ""}`)
        .join("; ");
      return `[${r.stateLabel}] ${r.name}${r.duration ? ` (${r.duration})` : ""}${builds ? ` — ${builds}` : ""}${r.reason ? `\n    ${r.reason.split("\n").join("\n    ")}` : ""}`;
    })
    .join("\n");
}

export interface AttemptSteps {
  package_name: string;
  version: string;
  compiler_version?: string | null;
  steps?: unknown;
  native_targets?: string | null;
  status?: string | null;
}

/** Page-level banner: the attempts whose checklist has a degraded / failed
 *  row (callers pass the current, non-historical attempts). */
export function attemptsWithProblems(
  attempts: readonly AttemptSteps[],
): Array<{ key: string; title: string; lines: string[] }> {
  const out: Array<{ key: string; title: string; lines: string[] }> = [];
  for (const a of attempts) {
    const parsed = parseSteps(a.steps);
    if (!parsed) continue;
    const lines = bannerLines(
      withDarwinFromTargets(parsed, a.native_targets, a.status),
    );
    if (lines.length === 0) continue;
    out.push({
      key: `${a.package_name}@${a.version}+${a.compiler_version ?? ""}`,
      title: `${a.package_name}@${a.version}`,
      lines,
    });
  }
  return out;
}
